import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Gift, Loader2, AlertCircle, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/layout/Navbar";
import { withReturnTo } from "@/lib/returnTo";

/**
 * Landing for a courtesy code printed on a QR card.
 *
 * The code survives the trip through registration in sessionStorage rather than
 * a query string: the signup flow already redirects twice (onboarding, then the
 * role split) and a query param does not survive that without threading it
 * through every hop. That threading now exists — `lib/returnTo.js` — but it
 * carries a *destination*, not a payload, so the code stays here and returnTo
 * only says which page to land on.
 */
export const PROMO_STORAGE_KEY = "fd_promo_code";

export default function Promo() {
  const { t } = useTranslation();
  const { code } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState("loading"); // loading | valid | invalid
  const [promo, setPromo] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/promo/${encodeURIComponent(code)}`);
        if (!alive) return;
        setPromo(data);
        setState("valid");
        // Remembered whether or not they are logged in: an anonymous visitor
        // registers first and the code has to still be here afterwards.
        try { sessionStorage.setItem(PROMO_STORAGE_KEY, data.code); } catch { /* private mode */ }
      } catch {
        if (alive) setState("invalid");
      }
    })();
    return () => { alive = false; };
  }, [code]);

  const goToPlans = () => {
    const role = user?.profile?.role;
    // `?promo=` was read by nothing — the code travels in sessionStorage, as
    // this file's own docstring says. What the parameter was really trying to
    // express is where to land afterwards, and that is what returnTo says.
    if (!user) navigate(withReturnTo("/signup/provider", "/provider/billing"));
    else if (role === "provider") navigate("/provider/billing");
    else navigate("/pricing");
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-linen flex items-center justify-center p-4" data-testid="promo-page">
        <div className="w-full max-w-lg">
          <div className="card-base p-8 text-center">
            {state === "loading" && (
              <Loader2 size={44} className="mx-auto text-smoke animate-spin" />
            )}

            {state === "invalid" && (
              <div data-testid="promo-invalid">
                <AlertCircle size={44} className="mx-auto text-red-600 mb-3" />
                <h1 className="font-display text-2xl font-bold text-worker">
                  {t("promo.invalidTitle")}
                </h1>
                <p className="mt-2 text-smoke text-sm">{t("promo.invalidBody")}</p>
                <button onClick={() => navigate("/pricing")} className="fx-btn fx-btn--secondary mt-6">
                  {t("promo.seePlans")}
                </button>
              </div>
            )}

            {state === "valid" && promo && (
              <div data-testid="promo-valid">
                <Gift size={44} className="mx-auto text-hive-ink mb-3" />
                <h1 className="font-display text-3xl font-bold text-worker">
                  {promo.free_period
                    ? t("promo.freeTitle", { months: promo.duration_in_months || 1 })
                    : t("promo.discountTitle")}
                </h1>
                <p className="mt-3 text-smoke">
                  {promo.free_period
                    ? t("promo.freeBody")
                    : t("promo.discountBody", { percent: promo.percent_off })}
                </p>

                <div className="mt-6 rounded-md bg-linen border border-worker/10 p-4">
                  <p className="text-xs uppercase tracking-wide text-smoke">
                    {t("promo.yourCode")}
                  </p>
                  <p className="font-mono text-2xl font-bold text-worker mt-1 break-all"
                     data-testid="promo-code">
                    {promo.code}
                  </p>
                </div>

                <button onClick={goToPlans} className="fx-btn fx-btn--primary mt-6 w-full justify-center"
                        data-testid="promo-cta">
                  {user ? t("promo.ctaLoggedIn") : t("promo.ctaAnonymous")}
                  <ArrowRight size={18} />
                </button>
                <p className="mt-3 text-xs text-smoke">{t("promo.cardNote")}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
